import React, { useState, useEffect, useMemo } from "react";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import "./personalNew.css";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import HijriDatePicker from "../employeesModals/HijriDatePicker";
import AsyncSelect from "react-select/async";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import { SalaryComponent } from "../employeesModals/SalaryComponent";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";

const SalaryNew = ({
  salaryDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [totalSalary, setTotalSalary] = useState("");
  const [deductionAmount, setDeductionAmount] = useState("");
  const {
    getReligionMaster,
    getCountryMaster,
    updatePersonalInfo,
    deactivateEmployee,
    updateEmployeeRegistrationMaster,
  } = MainServices();
  const [initialValues, setInitialValues] = useState({});

  const getValidationSchema = () => {
    return Yup.object().shape({
      basicPay: Yup.number()
        .typeError("Basic Pay must be a valid number")
        .required("Basic Pay is required")
        .min(0, "Basic Pay cannot be negative") // Allow 0 and positive numbers
        .max(9999999999, "Basic Pay is too large"),

      housingAllows: Yup.number()
        .typeError("Housing Allowance must be a valid number")
        .required("Housing Allowance is required")
        .min(0, "Housing Allowance cannot be negative") // Allow 0 and positive numbers
        .max(9999999999, "Housing Allowance is too large"),

      transportAllows: Yup.number()
        .typeError("Transport Allowance must be a valid number")
        .required("Transport Allowance is required")
        .min(0, "Transport Allowance cannot be negative") // Allow 0 and positive numbers
        .max(9999999999, "Transport Allowance is too large"),

      foodAllows: Yup.number()
        .typeError("Food Allowance must be a valid number")
        .required("Food Allowance is required")
        .min(0, "Food Allowance cannot be negative") // Allow 0 and positive numbers
        .max(9999999999, "Food Allowance is too large"),

      otherAllows: Yup.number()
        .typeError("Other Allowance must be a valid number")
        .required("Other Allowances are required")
        .min(0, "Other Allowance cannot be negative") // Allow 0 and positive numbers
        .max(9999999999, "Other Allowance is too large"),

      gosiDeduction: Yup.number()
        .nullable()
        .typeError("GOSI Deduction must be a valid number")
        .min(0, "GOSI Deduction cannot be negative") // Allow 0 and positive numbers
        .max(9999999999, "GOSI Deduction is too large")
        .when("citizen", {
          is: "true",
          then: (schema) => schema.required("GOSI Deduction is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
    });
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "56px",
      padding: "0 6px",
      borderRadius: "8px",
      display: "flex",
      justifyContent: "space-between",
      background: "#f9fafb",
      border: "1px solid #eaecf0",
      fontFamily: "Poppins, sans-serif",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "18px",
      textAlign: "left",
      color: "#111827",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#A0A0A0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#04080b",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? "#f3f4f6" : "#fff",
      color: "#04080b",
      padding: "10px 16px",
      cursor: "pointer",
      fontSize: "12px",
      lineHeight: "18px",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px",
      overflowY: "auto",
    }),
  };

  const convertAndFormatHijriDate = (dateString) => {
    // If the input is empty or invalid, return an empty string or a default value
    if (!dateString || typeof dateString !== "string") {
      return ""; // or return a default value like "MM-DD-YYYY"
    }

    // Check if the input is in ISO format (e.g., "1446-03-09T00:00:00.000Z")
    if (dateString.includes("T")) {
      // Extract the date part (YYYY-MM-DD) from the ISO string
      const datePart = dateString.split("T")[0];
      const [year, month, day] = datePart.split("-");

      // Validate year, month, and day
      if (!year || !month || !day) {
        return ""; // or return a default value like "MM-DD-YYYY"
      }

      // Format as "MM-DD-YYYY"
      return `${month}-${day}-${year}`;
    } else {
      // Check if the input is already in "MM-DD-YYYY" format
      const dateParts = dateString.split("-");
      if (
        dateParts.length === 3 &&
        dateParts[0].length === 2 &&
        dateParts[1].length === 2 &&
        dateParts[2].length === 4
      ) {
        return dateString; // Return as is if already in "MM-DD-YYYY" format
      } else {
        return ""; // or return a default value like "MM-DD-YYYY"
      }
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const updatedSalaryInfo = {
      ...(values?.basicPay && { basicPay: values.basicPay }),
      ...(values?.housingAllows && {
        housingAllows: values.housingAllows,
      }),
      ...(values?.transportAllows && {
        transportAllows: values.transportAllows,
      }),
      ...(values?.foodAllows && { foodAllows: values.foodAllows }),
      ...(values?.otherAllows && { otherAllows: values.otherAllows }),
      ...(totalSalary &&
        totalSalary !== "0.00" && { totalSalary: totalSalary }),
      ...(values?.citizen === "true" &&
        values?.gosiDeduction && {
          gosiDeduction: values.gosiDeduction,
        }),
    };

    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };
    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        salaryInfo: updatedSalaryInfo,
      },
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        setIsEditing(false);
        toast("Salary Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update salary", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (values) => {
    setInitialValues(values);
    setIsEditing(true); // Enable edit mode
  };

  const handleSetTotalSalary = (totalSalary) => {
    setTotalSalary(totalSalary);
  };

  const calculateGosiDeductionAmount = (values) => {
    let gosiDeductionAmount = 0;
    if (values?.citizen === "true") {
      gosiDeductionAmount = (
        ((Number(values?.basicPay) || 0) +
          (Number(values?.housingAllows) || 0)) *
        (Number(values?.gosiDeduction) / 100)
      ).toFixed(2);

      return gosiDeductionAmount;
    }
  };

  return (
    <>
      <div className="tab-section">
        <Formik
          enableReinitialize
          initialValues={{
            citizen: personalInfo?.citizen || "",
            basicPay: salaryDetails?.basicPay || "",
            housingAllows: salaryDetails?.housingAllows || "",
            transportAllows: salaryDetails?.transportAllows || "",
            foodAllows: salaryDetails?.foodAllows || "",
            otherAllows: salaryDetails?.otherAllows || "",
            totalSalary: salaryDetails?.totalSalary || "",
            gosiDeduction: salaryDetails?.gosiDeduction || "",
            deductionAmount: salaryDetails?.deductionAmount || "",
          }}
          validationSchema={getValidationSchema()}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => (
            <>
              <div className="tab-header-container">
                <h3 className="tab-heading">{t("Salary")}</h3>
                {currentEmployeeEditing ? (
                  <EditingRestrictionNotice message="Read-only" />
                ) : (
                  !isEditing && ( // Show edit button only when not in edit mode
                    <div
                      className="tab-header-edit-container"
                      onClick={() => handleEditClick(values)}
                    >
                      <img src={Edit} alt="edit icon" />
                      <span className="edit-txt">{t("Edit")}</span>
                    </div>
                  )
                )}
              </div>
              <Form>
                <div className="add-body-content-container-pr">
                  {/* <div className="add-body-content-header">
                    <h3 className="add-body-content-heading">Salary</h3>
                  </div> */}
                  <div className="add-body-content-grid">
                    {/* Basic Pay */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="basicPay">
                            {t("Basic Pay")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {isEditing ? (
                          <div className="add-input-wrapper">
                            <input
                              id="basicPay"
                              name="basicPay"
                              className="add-wrap-input"
                              placeholder="0"
                              maxLength={10}
                              value={values?.basicPay}
                              onChange={(e) =>
                                setFieldValue("basicPay", e.target.value)
                              }
                            />
                            <span className="add-input-in">{t("SAR")}</span>
                          </div>
                        ) : (
                          <div className="display-value">
                            {values.basicPay || t("N/A")}
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="basicPay"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Housing Allowance */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="housingAllows">
                            {t("Housing Allowance")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {isEditing ? (
                          <div className="add-input-wrapper">
                            <input
                              id="housingAllows"
                              name="housingAllows"
                              className="add-wrap-input"
                              placeholder="0"
                              maxLength={10}
                              value={values?.housingAllows}
                              onChange={(e) =>
                                setFieldValue("housingAllows", e.target.value)
                              }
                            />
                            <span className="add-input-in">{t("SAR")}</span>
                          </div>
                        ) : (
                          <div className="display-value">
                            {values.housingAllows || t("N/A")}
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="housingAllows"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Transport Allowance */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label
                            className="add-label"
                            htmlFor="transportAllows"
                          >
                            {t("Transport Allowance")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {isEditing ? (
                          <div className="add-input-wrapper">
                            <input
                              id="transportAllows"
                              name="transportAllows"
                              className="add-wrap-input"
                              placeholder="0"
                              maxLength={10}
                              value={values?.transportAllows}
                              onChange={(e) =>
                                setFieldValue("transportAllows", e.target.value)
                              }
                            />
                            <span className="add-input-in">{t("SAR")}</span>
                          </div>
                        ) : (
                          <div className="display-value">
                            {values.transportAllows || t("N/A")}
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="transportAllows"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Food Allowance */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="foodAllows">
                            {t("Food Allowance")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {isEditing ? (
                          <div className="add-input-wrapper">
                            <input
                              id="foodAllows"
                              name="foodAllows"
                              className="add-wrap-input"
                              placeholder="0"
                              maxLength={10}
                              value={values?.foodAllows}
                              onChange={(e) =>
                                setFieldValue("foodAllows", e.target.value)
                              }
                            />
                            <span className="add-input-in">{t("SAR")}</span>
                          </div>
                        ) : (
                          <div className="display-value">
                            {values.foodAllows || t("N/A")}
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="foodAllows"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Other Allowance */}
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="otherAllows">
                            {t("Other Allowance")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {isEditing ? (
                          <div className="add-input-wrapper">
                            <input
                              id="otherAllows"
                              name="otherAllows"
                              className="add-wrap-input"
                              placeholder="0"
                              maxLength={10}
                              value={values?.otherAllows}
                              onChange={(e) =>
                                setFieldValue("otherAllows", e.target.value)
                              }
                            />
                            <span className="add-input-in">{t("SAR")}</span>
                          </div>
                        ) : (
                          <div className="display-value">
                            {values.otherAllows || t("N/A")}
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="otherAllows"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                </div>

                {/* GOSI Deduction Section (for citizens) */}
                {values.citizen === "true" && (
                  <>
                    <div className="add-body-content-container-pr">
                      <div className="add-body-content-header">
                        <h3 className="add-body-content-heading">
                          {t("GOSI Deduction")}
                        </h3>
                      </div>
                      <div className="add-body-content-grid">
                        {/* GOSI Deduction */}
                        <div className="form-group">
                          <div className="add-body-input-section">
                            <div className="add-label-container">
                              <label
                                className="add-label"
                                htmlFor="gosiDeduction"
                              >
                                {t("GOSI Deduction")}
                              </label>
                              <span className="add-req-mark">*</span>
                            </div>
                            {isEditing ? (
                              <div className="add-input-wrapper">
                                <input
                                  id="gosiDeduction"
                                  name="gosiDeduction"
                                  className="add-wrap-input"
                                  placeholder="0"
                                  maxLength={10}
                                  value={values?.gosiDeduction}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "gosiDeduction",
                                      e.target.value
                                    )
                                  }
                                />
                                <span className="add-input-in">%</span>
                              </div>
                            ) : (
                              <div className="display-value">
                                {values.gosiDeduction || t("N/A")}
                              </div>
                            )}
                          </div>
                          <CustomErrorMessage
                            name="gosiDeduction"
                            errors={errors}
                            touched={touched}
                          />
                        </div>

                        {/* Deduction Amount */}
                        <div className="form-group">
                          <div className="add-body-input-section">
                            <div className="add-label-container">
                              <label
                                className="add-label"
                                htmlFor="deductionAmount"
                              >
                                {t("Deduction Amount")}
                              </label>
                              <span className="add-req-mark">*</span>
                            </div>
                            {isEditing ? (
                              <div className="add-input-wrapper">
                                <input
                                  disabled={true}
                                  id="deductionAmount"
                                  name="deductionAmount"
                                  className="add-wrap-input"
                                  placeholder="0"
                                  maxLength={10}
                                  value={values?.deductionAmount}
                                  onChange={(e) =>
                                    setFieldValue(
                                      "deductionAmount",
                                      e.target.value
                                    )
                                  }
                                />
                                <span className="add-input-in">{t("SAR")}</span>
                              </div>
                            ) : (
                              <div className="display-value">
                                {calculateGosiDeductionAmount(values)}
                              </div>
                            )}
                          </div>
                          <CustomErrorMessage
                            name="deductionAmount"
                            errors={errors}
                            touched={touched}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <SalaryComponent
                  totalSalary={totalSalary}
                  handleSetTotalSalary={handleSetTotalSalary}
                  values={values}
                  setFieldValue={setFieldValue}
                />
                {/* Submit button (only visible in edit mode) */}
                {isEditing && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          setIsEditing(false);
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      </div>
    </>
  );
};

export default SalaryNew;
